import React, { useState, useEffect } from 'react';
import {
  Container,
  Button,
  Card,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const WithdrawalRecords = () => {
  const [tab, setTab] = useState('deposit');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Fetch transactions data
    fetchTransactions(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, currentPage]);

  const fetchTransactions = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      let response;

      if (tab === 'application') {
        // Fetch PENDING withdrawal records
        response = await api.get(
          `/api/financial/withdrawals?status=PENDING&page=${page}&limit=5`
        );
      } else {
        // Fetch all withdrawal records
        response = await api.get(
          `/api/financial/withdrawals?page=${page}&limit=5`
        );
      }

      if (response.data.success) {
        // Map withdrawal data to transaction format
        const mappedTransactions = response.data.data.withdrawals.map(
          (withdrawal) => ({
            id: withdrawal.id,
            transactionId: withdrawal.transactionId,
            type: 'WITHDRAWAL',
            method: withdrawal.networkType || 'CRYPTO',
            amount: withdrawal.amount,
            currency: withdrawal.currencyCode,
            netAmount: withdrawal.netAmount,
            fee: withdrawal.fee,
            status: withdrawal.status,
            createdAt: withdrawal.createdAt,
            updatedAt: withdrawal.updatedAt,
            completedAt: withdrawal.processedAt,
            processingNote: withdrawal.adminNotes,
            toAddress: withdrawal.toAddress,
            transactionHash: withdrawal.transactionHash,
          })
        );

        setTransactions(mappedTransactions);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          'Failed to load transactions. Please try again.'
      );
      setTransactions([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: 'warning', text: 'Pending' },
      COMPLETED: { bg: 'success', text: 'Completed' },
      FAILED: { bg: 'danger', text: 'Failed' },
      REJECTED: { bg: 'danger', text: 'Rejected' },
    };
    const config = statusConfig[status] || { bg: 'secondary', text: status };
    return (
      <Badge bg={config.bg} className='px-2 py-1'>
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAmount = (amount, currency) => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination?.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const pages = [];
    let startPage = currentPage;
    let endPage = Math.min(pagination.totalPages, currentPage + 1);

    // Adjust if we're near the end
    if (currentPage === pagination.totalPages) {
      startPage = Math.max(1, currentPage - 1);
      endPage = currentPage;
    }

    // Previous button
    pages.push(
      <Button
        key='prev'
        variant='outline-primary'
        size={isMobile ? 'sm' : 'md'}
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        className='me-1'
        style={{ minWidth: isMobile ? '35px' : '40px' }}
      >
        <i className='bi bi-chevron-left'></i>
      </Button>
    );

    // Page numbers (only 2 at a time)
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? 'primary' : 'outline-primary'}
          size={isMobile ? 'sm' : 'md'}
          onClick={() => handlePageChange(i)}
          className='me-1'
          style={{
            minWidth: isMobile ? '35px' : '40px',
            fontWeight: currentPage === i ? '600' : '400',
          }}
        >
          {i}
        </Button>
      );
    }

    // Next button
    pages.push(
      <Button
        key='next'
        variant='outline-primary'
        size={isMobile ? 'sm' : 'md'}
        disabled={currentPage === pagination.totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
        style={{ minWidth: isMobile ? '35px' : '40px' }}
      >
        <i className='bi bi-chevron-right'></i>
      </Button>
    );

    return pages;
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: isMobile ? '1rem' : 'clamp(1rem, 3vw, 1.5rem)',
        backgroundColor: '#ffffff',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <div className='d-flex align-items-center mb-3 mb-md-4'>
        <Button
          variant='link'
          onClick={() => navigate(-1)}
          className='text-dark p-0 me-2'
          style={{ textDecoration: 'none' }}
        >
          <i className={`bi bi-arrow-left ${isMobile ? 'fs-5' : 'fs-4'}`}></i>
        </Button>
        <h4 className={`mb-0 fw-bold ${isMobile ? 'fs-5' : ''}`}>
          Transaction Records
        </h4>
      </div>

      {/* Tabs */}
      <div className='d-flex gap-2 mb-3 mb-md-4' style={{ flexWrap: 'wrap' }}>
        <Button
          variant={tab === 'application' ? 'primary' : 'outline-primary'}
          className={`${isMobile ? 'px-3 py-2' : 'px-4 py-2'} rounded-3`}
          onClick={() => handleTabChange('application')}
          style={{
            fontWeight: tab === 'application' ? '600' : '400',
            border: tab === 'application' ? 'none' : '1px solid #0d6efd',
            fontSize: isMobile ? '0.875rem' : '1rem',
          }}
        >
          Application Record
        </Button>
        <Button
          variant={tab === 'deposit' ? 'warning' : 'outline-warning'}
          className={`${isMobile ? 'px-3 py-2' : 'px-4 py-2'} rounded-3`}
          onClick={() => handleTabChange('deposit')}
          style={{
            fontWeight: tab === 'deposit' ? '600' : '400',
            border: tab === 'deposit' ? 'none' : '1px solid #ffc107',
            color: tab === 'deposit' ? '#000' : '#000000',
            fontSize: isMobile ? '0.875rem' : '1rem',
          }}
        >
          Withdrawal Record
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <Alert
          variant='danger'
          dismissible
          onClose={() => setError(null)}
          className='mb-3'
        >
          <i className='bi bi-exclamation-triangle-fill me-2'></i>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <div className='text-center py-5'>
          <Spinner animation='border' variant='primary' />
          <p className='mt-3 text-muted'>Loading transactions...</p>
        </div>
      ) : (
        <>
          {/* Transactions List */}
          {transactions.length === 0 ? (
            <Card className='border-0 shadow-sm'>
              <Card.Body className='text-center py-5'>
                <i className='bi bi-inbox fs-1 text-muted mb-3 d-block'></i>
                <h5 className='text-muted'>No Records Found</h5>
                <p className='text-muted mb-0'>
                  {tab === 'application'
                    ? 'No pending withdrawal records available at the moment.'
                    : 'No withdrawal records available at the moment.'}
                </p>
              </Card.Body>
            </Card>
          ) : (
            <div className='mb-3 mb-md-4'>
              {transactions.map((transaction) => (
                <Card
                  key={transaction.id}
                  className='mb-2 mb-md-3 border-0 shadow-sm'
                  style={{
                    borderRadius: isMobile ? '8px' : '12px',
                    overflow: 'visible',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow =
                        '0 8px 24px rgba(0,0,0,0.15)';
                      e.currentTarget.style.zIndex = '10';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow =
                        '0 1px 3px rgba(0,0,0,0.1)';
                      e.currentTarget.style.zIndex = '1';
                    }
                  }}
                >
                  <Card.Body className={isMobile ? 'p-2' : 'p-3'}>
                    {/* Transaction Header */}
                    <div className='d-flex justify-content-between align-items-start mb-2'>
                      <div className='d-flex align-items-center'>
                        <div
                          className={`${
                            isMobile ? 'me-2' : 'me-3'
                          } d-flex align-items-center justify-content-center`}
                          style={{
                            width: isMobile ? '40px' : '48px',
                            height: isMobile ? '40px' : '48px',
                            borderRadius: isMobile ? '8px' : '12px',
                            backgroundColor: '#FFF3E0',
                          }}
                        >
                          <i
                            className={`bi bi-arrow-up-circle ${
                              isMobile ? 'fs-5' : 'fs-4'
                            }`}
                            style={{
                              color: '#FF9800',
                            }}
                          ></i>
                        </div>
                        <div>
                          <h6
                            className={`mb-1 fw-bold ${isMobile ? 'fs-6' : ''}`}
                          >
                            {transaction.type}
                          </h6>
                          <small
                            className='text-muted'
                            style={{
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                            }}
                          >
                            {transaction.method}
                          </small>
                        </div>
                      </div>
                      {getStatusBadge(transaction.status)}
                    </div>

                    {/* Transaction Details */}
                    <div className='row g-2 mt-2'>
                      <div className='col-6'>
                        <small
                          className='text-muted d-block'
                          style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Amount
                        </small>
                        <strong
                          className={`text-primary ${
                            isMobile ? 'fs-6' : 'fs-6'
                          }`}
                        >
                          {formatAmount(
                            transaction.amount,
                            transaction.currency
                          )}
                        </strong>
                      </div>
                      <div className='col-6'>
                        <small
                          className='text-muted d-block'
                          style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Net Amount
                        </small>
                        <strong className={isMobile ? 'fs-6' : 'fs-6'}>
                          {formatAmount(
                            transaction.netAmount,
                            transaction.currency
                          )}
                        </strong>
                      </div>
                      <div className='col-6'>
                        <small
                          className='text-muted d-block'
                          style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Fee
                        </small>
                        <span
                          style={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                        >
                          {formatAmount(transaction.fee, transaction.currency)}
                        </span>
                      </div>
                      <div className='col-6'>
                        <small
                          className='text-muted d-block'
                          style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Date
                        </small>
                        <span
                          className='small'
                          style={{
                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                          }}
                        >
                          {formatDate(transaction.createdAt)}
                        </span>
                      </div>
                      {transaction.toAddress && (
                        <div className='col-12'>
                          <small
                            className='text-muted d-block'
                            style={{
                              fontSize: isMobile ? '0.7rem' : '0.75rem',
                            }}
                          >
                            To Address
                          </small>
                          <code
                            className='small'
                            style={{
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                              wordBreak: 'break-all',
                            }}
                          >
                            {transaction.toAddress}
                          </code>
                        </div>
                      )}
                      {transaction.completedAt && (
                        <div className='col-12'>
                          <small
                            className='text-muted d-block'
                            style={{
                              fontSize: isMobile ? '0.7rem' : '0.75rem',
                            }}
                          >
                            Completed At
                          </small>
                          <span
                            className='small text-success'
                            style={{
                              fontSize: isMobile ? '0.75rem' : '0.875rem',
                            }}
                          >
                            {formatDate(transaction.completedAt)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Transaction ID */}
                    <div
                      className={`${
                        isMobile ? 'mt-2 pt-2' : 'mt-3 pt-2'
                      } border-top`}
                    >
                      <small
                        className='text-muted'
                        style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      >
                        Transaction ID
                      </small>
                      <div className='d-flex align-items-center justify-content-between'>
                        <code
                          className='small text-truncate'
                          style={{
                            maxWidth: '85%',
                            fontSize: isMobile ? '0.65rem' : '0.75rem',
                          }}
                        >
                          {transaction.id}
                        </code>
                        <Button
                          variant='link'
                          size='sm'
                          className='p-0 text-muted'
                          onClick={() => {
                            navigator.clipboard.writeText(transaction.id);
                            // Optional: Show a toast notification
                          }}
                          title='Copy ID'
                        >
                          <i
                            className={`bi bi-clipboard ${
                              isMobile ? 'fs-6' : ''
                            }`}
                          ></i>
                        </Button>
                      </div>
                    </div>

                    {/* Transaction Hash */}
                    {transaction.transactionHash && (
                      <div
                        className={`${
                          isMobile ? 'mt-2 pt-2' : 'mt-3 pt-2'
                        } border-top`}
                      >
                        <small
                          className='text-muted'
                          style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                        >
                          Transaction Hash
                        </small>
                        <div className='d-flex align-items-center justify-content-between'>
                          <code
                            className='small text-truncate'
                            style={{
                              maxWidth: '85%',
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                            }}
                          >
                            {transaction.transactionHash}
                          </code>
                          <Button
                            variant='link'
                            size='sm'
                            className='p-0 text-muted'
                            onClick={() => {
                              navigator.clipboard.writeText(
                                transaction.transactionHash
                              );
                            }}
                            title='Copy Hash'
                          >
                            <i
                              className={`bi bi-clipboard ${
                                isMobile ? 'fs-6' : ''
                              }`}
                            ></i>
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Processing Note */}
                    {transaction.processingNote && (
                      <div
                        className={`${
                          isMobile ? 'mt-2' : 'mt-2'
                        } p-2 bg-light rounded`}
                      >
                        <small
                          className='text-muted'
                          style={{
                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                          }}
                        >
                          <i className='bi bi-info-circle me-1'></i>
                          Note: {transaction.processingNote}
                        </small>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {pagination && transactions.length > 0 && (
            <>
              {/* Pagination Buttons and Info */}
              <Card className='border-0 shadow-sm'>
                <Card.Body className={isMobile ? 'py-2' : 'py-3'}>
                  <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                    {/* Page Info - LEFT SIDE */}
                    <div
                      className='text-muted'
                      style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      <i className='bi bi-file-text me-1'></i>
                      Page <strong>{pagination.currentPage}</strong> of{' '}
                      <strong>{pagination.totalPages}</strong>
                    </div>

                    {/* Pagination Buttons - RIGHT SIDE */}
                    <div className='d-flex justify-content-center align-items-center flex-wrap gap-1'>
                      {renderPagination()}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default WithdrawalRecords;
